import React, { useState } from "react";
import { Modal, Button } from "@mui/material";
import {
  activateExternalDataset,
  rejectExternalDataset,
} from "../../../../../../apis/OpenCircApi";
import Swal from "sweetalert2";
import "./EmissionsgrundlagPanel.css";

const EmissionsgrundlagPanel = ({
  component,
  productId,
  isOpen,
  onClose,
  onDatasetActivated,
  onDatasetRejected,
  componentDropdown = [],
  bsddDropdown = [],
}) => {
  const [activating, setActivating] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  if (!isOpen) return null;
  
  // If no component, don't show panel
  if (!component) {
    return null;
  }

  const hasPending = component.external_dataset_state === "PENDING";
  const isActive = component.external_dataset_state === "ACTIVE";
  const pendingDataset = component.pending_dataset;
  const activeDataset = component.active_dataset;

  // Get component name from dropdown (database component for calculations)
  const getComponentName = () => {
    if (!component.component) return null;
    
    const componentCode = typeof component.component === "string" 
      ? component.component 
      : component.component?.value || component.component;
    
    // Always use database dropdown for component name (calculations use database component)
    const foundComponent = componentDropdown.find(
      (option) =>
        !option.isCategory &&
        (option.code === componentCode ||
          option.value === componentCode ||
          option.name === componentCode)
    );
    
    return foundComponent?.name || null;
  };

  const componentName = getComponentName();

  // Check if component is from bSDD dropdown
  const isBSDDComponent = () => {
    if (!component.component) return false;
    
    const componentCode = typeof component.component === "string" 
      ? component.component 
      : component.component?.value || component.component;
    
    // Check if component exists in bSDD dropdown
    const foundInBSDD = bsddDropdown.find(
      (option) =>
        !option.isCategory &&
        (option.code === componentCode ||
          option.value === componentCode ||
          option.name === componentCode)
    );
    
    return !!foundInBSDD;
  };

  const isFromBSDD = isBSDDComponent();

  // Get current emission factor (from database or active dataset)
  const getCurrentEmissionFactor = () => {
    // If active external dataset exists, use its factor
    if (isActive && activeDataset) {
      return activeDataset.gwp_factor;
    }
    
    // Otherwise, look up the component in the dropdown to get global_warming from characterization_factors
    if (component.component) {
      const componentCode = typeof component.component === "string" 
        ? component.component 
        : component.component?.value || component.component;
      
      // Check both dropdowns to find the component
      const allDropdowns = [...componentDropdown, ...bsddDropdown];
      const foundComponent = allDropdowns.find(
        (option) =>
          !option.isCategory &&
          (option.code === componentCode ||
            option.value === componentCode ||
            option.name === componentCode)
      );
      
      // Get global_warming from characterization_factors
      if (foundComponent?.characterization_factors?.global_warming !== undefined) {
        return foundComponent.characterization_factors.global_warming;
      }
    }
    
    // Fallback to component's own characterization_factors if available
    return component.characterization_factors?.global_warming || 0;
  };

  const currentFactor = getCurrentEmissionFactor();
  const newFactor = pendingDataset?.gwp_factor;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("da-DK", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }).format(date);
    } catch (e) {
      return dateString;
    }
  };

  const handleActivate = async () => {
    setActivating(true);

    activateExternalDataset({
      productId,
      componentId: component.id,
      datasetId: pendingDataset?.dataset_id,
      onSuccess: (updatedComponent) => {
        setActivating(false);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: `Dataset aktiveret. Emission factor opdateret til ${updatedComponent.active_dataset?.gwp_factor} kg CO2e/kg`,
          timer: 3000,
          showConfirmButton: false,
        });
        if (onDatasetActivated) {
          onDatasetActivated(updatedComponent);
        }
      },
      onError: (error) => {
        setActivating(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: `Kunne ikke aktivere dataset: ${error}`,
        });
      },
    });
  };

  const handleReject = async () => {
    setRejecting(true);

    rejectExternalDataset({
      productId,
      componentId: component.id,
      datasetId: pendingDataset?.dataset_id,
      onSuccess: (updatedComponent) => {
        setRejecting(false);
        Swal.fire({
          icon: "info",
          title: "Info",
          text: "Dataset afvist. Bruger database emission factors.",
          timer: 2000,
          showConfirmButton: false,
        });
        if (onDatasetRejected) {
          onDatasetRejected(updatedComponent);
        }
      },
      onError: (error) => {
        setRejecting(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: `Kunne ikke afvise dataset: ${error}`,
        });
      },
    });
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      className="emissionsgrundlag-modal"
      closeAfterTransition
    >
      <div className="emissionsgrundlag-panel">
        {/* Header */}
        <div className="panel-header">
          <h2>Emissionsgrundlag</h2>
          <button
            type="button"
            className="close-button"
            onClick={onClose}
            aria-label="Luk"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="panel-content">
          {/* Section A: Current Data Source */}
          <div className="panel-section">
            <h3>A - AKTUEL DATAKILDE</h3>
            <div className="info-box">
              <div className="info-row">
                <span className="info-label">Kildetype:</span>
                <span className="info-value">
                  {isActive
                    ? `Ekstern data (${activeDataset?.source || "OpenFrame"})`
                    : isFromBSDD
                    ? "Baggrundsdatabase (table6)"
                    : "Baggrundsdatabase (BONSAI)"}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Emission Factor (Global Warming):</span>
                <span className="info-value">
                  {currentFactor} kg CO2e/kg
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Komponent:</span>
                <span className="info-value">
                  {componentName ? (
                    <>
                      {componentName}
                      <br />
                      <span style={{ fontSize: "0.9em", color: "#666" }}>
                        {component.component}
                      </span>
                    </>
                  ) : (
                    component.component
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Section B: Hybrid Status */}
          <div className="panel-section">
            <h3>B - HYBRID-STATUS</h3>
            <div className="status-text">
              {isActive || hasPending
                ? "Fuldt dækket af valgt dataset"
                : "Ikke hybrid"}
            </div>
          </div>

          {/* Section C: Status */}
          <div className="panel-section">
            <h3>C - STATUS</h3>
            <div
              className={`status-text status-${component.external_dataset_state?.toLowerCase() || "none"}`}
            >
              {isActive && "Aktiv"}
              {hasPending && "Afventer aktivering af ekstern data"}
              {!isActive && !hasPending && "Ingen ekstern data"}
            </div>
            {/* Status is based on component.external_dataset_state:
                - "ACTIVE" → "Aktiv" (external dataset is active)
                - "PENDING" → "Afventer aktivering af ekstern data" (pending dataset waiting for activation)
                - null/undefined/"NONE"/other → "Ingen ekstern data" (no external dataset found or synced)
            */}
          </div>

          {/* Divider */}
          {hasPending && <div className="panel-divider" />}

          {/* New External Data Section */}
          {hasPending && pendingDataset && (
            <div className="panel-section new-data-section">
              <h3 className="new-data-header">
                <span className="new-data-badge">NY EKSTERN DATA FUNDET</span>
              </h3>
              <div className="dataset-details">
                <div className="detail-row">
                  <span className="detail-label">Kilde:</span>
                  <span className="detail-value">{pendingDataset.source}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Dataset ID:</span>
                  <span className="detail-value">{pendingDataset.dataset_id}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">År:</span>
                  <span className="detail-value">{pendingDataset.year}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Moduler:</span>
                  <span className="detail-value">
                    {pendingDataset.module_coverage}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Standard:</span>
                  <span className="detail-value">{pendingDataset.standard}</span>
                </div>
                {pendingDataset.br18_table6_version && (
                  <div className="detail-row">
                    <span className="detail-label">Version:</span>
                    <span className="detail-value">
                      {pendingDataset.br18_table6_version}
                    </span>
                  </div>
                )}
                <div className="detail-row highlight">
                  <span className="detail-label">Emission Factor:</span>
                  <span className="detail-value">
                    <strong>{pendingDataset.gwp_factor}</strong>{" "}
                    {pendingDataset.gwp_unit}
                  </span>
                </div>
                {pendingDataset.issued_at && (
                  <div className="detail-row">
                    <span className="detail-label">Udstedt:</span>
                    <span className="detail-value">
                      {formatDate(pendingDataset.issued_at)}
                    </span>
                  </div>
                )}
              </div>

              {/* Comparison */}
              <div className="comparison-box">
                <div className="comparison-item">
                  <span className="comparison-label">Nuværende:</span>
                  <span className="comparison-value">{currentFactor} kg CO2e/kg</span>
                </div>
                <div className="comparison-arrow">→</div>
                <div className="comparison-item new">
                  <span className="comparison-label">Ny:</span>
                  <span className="comparison-value">
                    <strong>{newFactor}</strong> kg CO2e/kg
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="panel-actions">
                <Button
                  variant="contained"
                  className="activate-button"
                  onClick={handleActivate}
                  disabled={activating || rejecting}
                >
                  {activating ? "Aktiverer..." : "Aktivér dataset"}
                </Button>
                <Button
                  variant="outlined"
                  className="reject-button"
                  onClick={handleReject}
                  disabled={activating || rejecting}
                >
                  {rejecting ? "Afviser..." : "Afvis"}
                </Button>
              </div>
            </div>
          )}

          {/* Active Dataset Info (if active) */}
          {isActive && activeDataset && (
            <div className="panel-section active-data-section">
              <div className="dataset-details">
                <div className="detail-row">
                  <span className="detail-label">Aktiv Dataset:</span>
                  <span className="detail-value">{activeDataset.dataset_id}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Kilde:</span>
                  <span className="detail-value">{activeDataset.source}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Emission Factor:</span>
                  <span className="detail-value">
                    {activeDataset.gwp_factor} {activeDataset.gwp_unit}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default EmissionsgrundlagPanel;


